import React, { useState } from 'react';
import { Search, Droplets, Sprout, Calculator, TrendingUp, CloudRain, Sun, Thermometer, BarChart3, Map, ArrowRight } from 'lucide-react';
import { motion } from 'framer-motion';
import ETOCalculator from './components/ETOCalculator';
import SoilTextureCalculator from './components/SoilTextureCalculator';
import KcDatabase from './components/KcDatabase';
import KMLConverter from './components/KMLConverter';
import GDDCalculator from './components/GDDCalculator';
import CalibracionCalculator from './components/CalibracionCalculator';
import OpenCanales from './components/OpenCanales';
import OpenRiego from './components/OpenRiego';
import FeaturedPlatformsCarousel from './components/FeaturedPlatformsCarousel';

interface Tool {
  id: string;
  title: string;
  description: string;
  category: string;
  icon: React.ReactNode;
  image: string;
  readTime: string;
}

function App() {
  const [searchQuery, setSearchQuery] = useState('');
  const [activeCalculator, setActiveCalculator] = useState<string | null>(null);
  const [activeCategory, setActiveCategory] = useState<string>('Todas');

  const tools: Tool[] = [
  {
    id: '1',
    title: 'Evapotranspiración de Referencia (ET0)',
    description: 'Calcula la ET0 usando el método de Penman-Monteith FAO-56 con datos climáticos precisos para una gestión eficiente del riego.',
    category: 'Evapotranspiración',
    icon: <Sun className="w-6 h-6" />,
    image: 'https://images.unsplash.com/photo-1592982537447-7440770cbfc9?w=800&q=80',
    readTime: '3 minutos'
  },
  {
    id: '2',
    title: 'Textura del Suelo',
    description: 'Determina la clasificación textural del suelo mediante análisis granulométrico de arena, limo y arcilla según estándares USDA.',
    category: 'Análisis',
    icon: <Sprout className="w-6 h-6" />,
    image: 'https://images.unsplash.com/photo-1574943320219-553eb213f72d?w=800&q=80',
    readTime: '2 minutos'
  },
  {
    id: '3',
    title: 'Coeficientes de Cultivo FAO(Kc)-1998',
    description: 'Accede a la base de datos completa de coeficientes Kc para diferentes cultivos y etapas fenológicas basados en FAO-56.',
    category: 'Coeficientes',
    icon: <Sprout className="w-6 h-6" />,
    image: 'https://images.unsplash.com/photo-1625246333195-78d9c38ad449?w=800&q=80',
    readTime: '5 minutos'
  },
  {
    id: '4',
    title: 'Base de Datos a KML',
    description: 'Convierte tus bases de datos en formato Excel o CSV a archivos KML para visualización en Google Earth sin programas externos.',
    category: 'Conversión',
    icon: <Map className="w-6 h-6" />,
    image: 'https://images.unsplash.com/photo-1524661135-423995f22d0b?w=800&q=80',
    readTime: '4 minutos'
  },
  {
    id: '5',
    title: 'Grados Día de Desarrollo (GDD)',
    description: 'Consulta la base de datos de unidades térmicas y calcula los grados día acumulados para predecir el desarrollo fenológico de tus cultivos.',
    category: 'Análisis',
    icon: <Thermometer className="w-6 h-6" />,
    image: 'https://images.unsplash.com/photo-1464226184884-fa280b87c399?w=800&q=80',
    readTime: '4 minutos'
  },
  {
    id: '8',
    title: 'Calibración de Índices de Vegetación',
    description: 'Correlaciona índices de vegetación (NDVI, EVI, etc.) con variables de campo usando modelos lineal, cuadrático, potencial, exponencial y logarítmico. Soporta uno o dos índices.',
    category: 'Análisis',
    icon: <TrendingUp className="w-6 h-6" />,
    image: 'https://images.unsplash.com/photo-1461749280684-dccba630e2f6?w=800&q=80',
    readTime: '5 minutos'
  },
  {
    id: '6',
    title: 'openCanales - Hidráulica de Canales',
    description: 'Estima parámetros hidráulicos en canales de riego: caudal, flujo normal, flujo crítico, salto hidráulico y más para secciones trapeciales, rectangulares, triangulares y circulares.',
    category: 'Hidráulica',
    icon: <Droplets className="w-6 h-6" />,
    image: 'https://images.unsplash.com/photo-1504297050568-910d24c426d3?w=800&q=80',
    readTime: '10 minutos'
  }];


  const filteredTools = tools.filter((tool) => {
    // Excluir openCanales del grid porque ya se muestra en las cards de plataformas destacadas
    if (tool.id === '6') return false;
    // Excluir ETO (id '1') y Coeficientes Kc (id '3') porque ahora viven dentro de openRiego
    if (tool.id === '1' || tool.id === '3') return false;
    const matchesSearch = tool.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    tool.description.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = activeCategory === 'Todas' || tool.category === activeCategory;
    return matchesSearch && matchesCategory;
  });

  const categories = ['Todas', ...Array.from(new Set(tools.map((tool) => tool.category)))];
  const featuredTool = tools[0];

  if (activeCalculator === '1') {
    return <ETOCalculator onBack={() => setActiveCalculator(null)} />;
  }

  if (activeCalculator === '2') {
    return <SoilTextureCalculator onBack={() => setActiveCalculator(null)} />;
  }

  if (activeCalculator === '3') {
    return <KcDatabase onBack={() => setActiveCalculator(null)} />;
  }

  if (activeCalculator === '4') {
    return <KMLConverter onBack={() => setActiveCalculator(null)} />;
  }

  if (activeCalculator === '5') {
    return <GDDCalculator onBack={() => setActiveCalculator(null)} />;
  }

  if (activeCalculator === '6') {
    return <OpenCanales onBack={() => setActiveCalculator(null)} />;
  }

  if (activeCalculator === '7') {
    return <OpenRiego onBack={() => setActiveCalculator(null)} />;
  }

  if (activeCalculator === '8') {
    return <CalibracionCalculator onBack={() => setActiveCalculator(null)} />;
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section with Background Image */}
      <div className="relative h-[500px] bg-gradient-to-br from-slate-900 to-blue-900 overflow-hidden">
        <div className="absolute inset-0 bg-black/40"></div>
        <img
          src="https://images.unsplash.com/photo-1625246333195-78d9c38ad449?w=1600&q=80"
          alt="Agriculture background"
          className="absolute inset-0 w-full h-full object-cover mix-blend-overlay opacity-100"></img>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-full flex flex-col justify-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-3xl">

            <h1 className="text-5xl md:text-6xl font-bold text-white mb-6 leading-tight">
              Herramientas HH
            </h1>
            <p className="text-xl md:text-2xl text-blue-100 leading-relaxed">
              Descubre las últimas herramientas, calculadoras y recursos para optimizar la gestión hídrica y agricultura de precisión.
            </p>
          </motion.div>
        </div>
      </div>

      {/* Filter Tabs */}
      <div className="border-b border-gray-200 bg-white sticky top-0 z-10 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="flex space-x-8 overflow-x-auto py-4" aria-label="Tabs">
            {categories.map((category) =>
            <button
              key={category}
              onClick={() => setActiveCategory(category)}
              className={`whitespace-nowrap pb-4 px-1 border-b-2 font-medium text-sm transition-colors ${
              activeCategory === category ?
              'border-blue-600 text-blue-600' :
              'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`
              }>

                {category}
              </button>
            )}
          </nav>
        </div>
      </div>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        {/* Featured Tool */}
        









































        {/* Featured Platforms Carousel */}
        <FeaturedPlatformsCarousel
          onOpenCanales={() => setActiveCalculator('6')}
          onOpenRiego={() => setActiveCalculator('7')} />


        {/* Section Title */}
        <div className="mb-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-3">Herramientas & Calculadoras</h2>
          <p className="text-lg text-gray-600">
            Mantente actualizado con las herramientas de OpenHH y cómo apoyamos a personas, negocios y el planeta.
          </p>
        </div>

        {/* Tools Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {filteredTools.map((tool, index) =>
          <motion.article
            key={tool.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            onClick={() => setActiveCalculator(tool.id)}
            className="group bg-white rounded-xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 cursor-pointer border border-gray-100">

              {/* Image */}
              <div className="relative h-48 overflow-hidden bg-gray-100">
                <img
                src={tool.image}
                alt={tool.title}
                className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />

              </div>

              {/* Content */}
              <div className="p-6">
                <div className="flex items-center gap-2 text-blue-600 text-sm font-semibold mb-3">
                  {tool.icon}
                  <span className="uppercase tracking-wide">{tool.category}</span>
                </div>

                <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-blue-600 transition-colors line-clamp-2">
                  {tool.title}
                </h3>

                <p className="text-gray-600 mb-4 line-clamp-3 leading-relaxed">
                  {tool.description}
                </p>

                <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-green-500 flex items-center justify-center">
                      <span className="text-white text-xs font-bold">HH</span>
                    </div>
                    <div>
                      <p className="text-xs font-semibold text-gray-900">OpenHH</p>
                      <p className="text-xs text-gray-500">{tool.readTime}</p>
                    </div>
                  </div>
                </div>
              </div>
            </motion.article>
          )}
        </div>

        {/* No Results */}
        {filteredTools.length === 0 &&
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="text-center py-16">

            <p className="text-xl text-gray-600 mb-2">No se encontraron herramientas</p>
            <p className="text-gray-500">Intenta con otra categoría</p>
          </motion.div>
        }
      </main>

      {/* CTA Section */}
      <div className="relative mt-20 bg-gradient-to-br from-blue-900 to-green-900 overflow-hidden">
        <div className="absolute inset-0 bg-black/30"></div>
        <img
          src="https://images.unsplash.com/photo-1625246333195-78d9c38ad449?w=1600&q=80"
          alt="CTA background"
          className="absolute inset-0 w-full h-full object-cover mix-blend-overlay opacity-20" />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 text-center">
          <h2 className="text-4xl font-bold text-white mb-4">
            ¿Necesitas ayuda personalizada?
          </h2>
          <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
            ¿Tienes un desafío específico que resolver? Ponte en contacto con el equipo de OpenHH para descubrir cómo podemos ayudarte.
          </p>
          <a href="https://www.openhh.mx/contact" className="inline-flex items-center gap-2 bg-white text-blue-900 px-8 py-4 rounded-lg font-semibold hover:bg-blue-50 transition-colors shadow-lg">
            Contáctanos hoy
            <ArrowRight className="w-5 h-5" />
          </a>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-gray-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="text-center">
            <p className="text-gray-400">© 2025 OpenHH - Herramientas para gestión hídrica y agricultura de precisión

            </p>
          </div>
        </div>
      </footer>
    </div>);

}

export default App;